import { useState } from "react";
import { PriorityComment } from "@/types";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { CommentForm } from "./CommentForm";

interface CommentItemProps {
  comment: PriorityComment;
  onUpdate: (commentPid: string, content: string, onSuccess: () => void) => void;
  onDelete: (commentPid: string) => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

export function CommentItem({
  comment,
  onUpdate,
  onDelete,
  isUpdating,
  isDeleting,
}: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const isEdited =
    comment.updatedAt &&
    comment.createdAt &&
    new Date(comment.updatedAt).getTime() - new Date(comment.createdAt).getTime() > 1000;

  const relativeTime = formatDistanceToNow(new Date(comment.createdAt), {
    addSuffix: true,
  });

  const handleUpdate = (content: string) => {
    onUpdate(comment.pid, content, () => setIsEditing(false));
  };

  const handleDelete = () => {
    onDelete(comment.pid);
    setIsDeleteConfirmOpen(false);
  };

  return (
    <>
      <div className="group rounded-lg border border-border bg-card p-2.5 transition-colors hover:bg-card/80">
        <div className="flex gap-2.5">
          <UserAvatar
            src={comment.user?.avatarUrl}
            name={comment.user?.fullName}
            className="mt-0.5 h-7 w-7 shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="mb-0.5 flex items-baseline justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <span className="truncate text-[13px] font-medium text-foreground">
                  {comment.user?.fullName ?? "Unknown"}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {relativeTime}
                </span>
                {isEdited && (
                  <span className="text-xs text-muted-foreground italic shrink-0">
                    edited
                  </span>
                )}
              </div>
              {comment.isOwner && !isEditing && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 text-muted-foreground opacity-70 group-hover:opacity-100"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setIsEditing(true)}>
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setIsDeleteConfirmOpen(true)}
                    >
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {isEditing ? (
              <CommentForm
                onSubmit={handleUpdate}
                isSubmitting={isUpdating}
                initialValue={comment.content}
                onCancel={() => setIsEditing(false)}
              />
            ) : (
              <p className="whitespace-pre-wrap break-words text-[13px] leading-5 text-foreground/90">
                {comment.content}
              </p>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete comment?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Your comment will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
